import { create } from 'zustand';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import {
  fetchSignatures,
  createSignature as createSignatureApi,
  updateSignature as updateSignatureApi,
  deleteSignature as deleteSignatureApi,
  type SignaturePayload,
} from '@/api/signatures';
import type { Signature } from '@/types/email';
import { getErrorMessage } from '@/lib/error';

const sortSignatures = (items: Signature[]): Signature[] => {
  return [...items].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
};

interface SignatureStoreState {
  signatures: Signature[];
  loading: boolean;
  loaded: boolean;
  error: string | null;
  saving: boolean;
  deletingId: string | null;
  loadSignatures: () => Promise<Signature[]>;
  refreshSignatures: () => Promise<Signature[]>;
  createSignature: (payload: SignaturePayload) => Promise<Signature>;
  updateSignature: (signatureId: string, payload: Partial<SignaturePayload>) => Promise<Signature>;
  deleteSignature: (signatureId: string) => Promise<void>;
  clearSignatures: () => void;
  setError: (message: string | null) => void;
}

export const useSignatureStore = create<SignatureStoreState>()(
  conditionalDevtools((set, get) => ({
    signatures: [],
    loading: false,
    loaded: false,
    error: null,
    saving: false,
    deletingId: null,

    setError: (message) => set({ error: message }),

    loadSignatures: async () => {
      set({ loading: true, error: null });
      try {
        const data = await fetchSignatures();
        const sorted = sortSignatures(data);
        set({
          signatures: sorted,
          loading: false,
          loaded: true,
        });
        return sorted;
      } catch (error) {
        set({
          loading: false,
          error: getErrorMessage(error) ?? 'Failed to load signatures',
        });
        throw error;
      }
    },

    refreshSignatures: async () => {
      return get().loadSignatures();
    },

    createSignature: async (payload) => {
      set({ saving: true });
      try {
        const created = await createSignatureApi(payload);
        set((state) => ({
          signatures: sortSignatures([...state.signatures, created]),
        }));
        return created;
      } catch (error) {
        set({
          error: getErrorMessage(error) ?? 'Failed to create signature',
        });
        throw error;
      } finally {
        set({ saving: false });
      }
    },

    updateSignature: async (signatureId, payload) => {
      set({ saving: true });
      try {
        const updated = await updateSignatureApi(signatureId, payload);
        set((state) => ({
          signatures: sortSignatures(
            state.signatures.map((signature) => (signature.id === updated.id ? updated : signature))
          ),
        }));
        return updated;
      } catch (error) {
        set({
          error: getErrorMessage(error) ?? 'Failed to update signature',
        });
        throw error;
      } finally {
        set({ saving: false });
      }
    },

    deleteSignature: async (signatureId) => {
      set({ deletingId: signatureId });
      try {
        await deleteSignatureApi(signatureId);
        set((state) => ({
          signatures: state.signatures.filter((signature) => signature.id !== signatureId),
        }));
      } catch (error) {
        set({
          error: getErrorMessage(error) ?? 'Failed to delete signature',
        });
        throw error;
      } finally {
        set({ deletingId: null });
      }
    },

    clearSignatures: () =>
      set({
        signatures: [],
        loading: false,
        loaded: false,
        error: null,
        saving: false,
        deletingId: null,
      }),
  }))
);
